import { createContext, useCallback, useContext, useMemo, useState } from "react";
import api from "./api";
import UploadProgressOverlay from "@/components/UploadProgressOverlay";

const UploadProgressCtx = createContext(null);

const noop = {
  begin() {},
  setPercent() {},
  end() {},
};

export function UploadProgressProvider({ children }) {
  const [state, setState] = useState({ open: false, percent: 0 });
  const begin = useCallback(() => setState({ open: true, percent: 0 }), []);
  const setPercent = useCallback((n) => {
    const pct = Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
    setState((s) => (s.open ? { ...s, percent: pct } : s));
  }, []);
  const end = useCallback(() => setState({ open: false, percent: 0 }), []);
  const value = useMemo(() => ({ begin, setPercent, end }), [begin, setPercent, end]);
  return (
    <UploadProgressCtx.Provider value={value}>
      {children}
      <UploadProgressOverlay open={state.open} percent={state.percent} />
    </UploadProgressCtx.Provider>
  );
}

export function useUploadProgress() {
  return useContext(UploadProgressCtx) || noop;
}

export function axiosUploadConfig(setPercent, extra = {}) {
  return {
    timeout: extra.timeout || 180000,
    onUploadProgress: (e) => {
      if (e.total) setPercent(Math.round((e.loaded / e.total) * 100));
    },
    ...extra,
  };
}

/** POST a FormData payload and drive the global “please wait uploading %” overlay. */
export function useFileUpload() {
  const { begin, setPercent, end } = useUploadProgress();
  return useCallback(async (url, formData, extra = {}) => {
    begin();
    try {
      const { data } = await api.post(url, formData, axiosUploadConfig(setPercent, extra));
      setPercent(100);
      return data;
    } finally {
      end();
    }
  }, [begin, setPercent, end]);
}
